import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Input,
} from '@angular/core';
import { AnimationBehavior, DirectivesModule } from '@freelancer/directives';
import { ColumnComponent } from '../column/column.component';
import { ContainerComponent, ContainerSize } from '../container';
import { VerticalAlignment } from '../grid';
import { GridComponent } from '../grid/grid.component';
import type { PictureBackgroundImageStyles } from '../picture';
import { PictureComponent, PictureBgImageObjectFit } from '../picture';
import { SectionHomepageFeatureFooterComponent } from './section-homepage-feature-footer.component';

@Component({
  standalone: true,
  imports: [
    ContainerComponent,
    DirectivesModule,
    GridComponent,
    ColumnComponent,
    PictureComponent,
  ],
  selector: 'fl-section-homepage-feature',
  template: `
    <fl-container
      [size]="ContainerSize.DESKTOP_LARGE"
      [sizeDesktopXLarge]="ContainerSize.DESKTOP_XLARGE"
      class="AnimationContainer"
    >
      <div
        class="AnimatedLineTop"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.GROW"
        [flAnimateOnScrollDelay]="0.5"
        [flAnimateOnScrollDuration]="0.3"
      ></div>
      <div
        class="AnimatedLogo"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
        [flAnimateOnScrollDuration]="0.2"
        [flAnimateOnScrollDelay]="0.8"
      ></div>
      <div
        class="AnimatedLineBottom"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.GROW"
        [flAnimateOnScrollDelay]="0.8"
        [flAnimateOnScrollDuration]="0.5"
      ></div>
    </fl-container>
    <fl-container
      class="Container"
      [size]="ContainerSize.DESKTOP_LARGE"
      [sizeDesktopXLarge]="ContainerSize.DESKTOP_XLARGE"
    >
      <fl-picture
        class="mobile:hide"
        [isBackgroundImage]="true"
        [srcTablet]="srcBackgroundTablet"
        [srcLargeDesktop]="srcBackgroundLargeDesktop"
        [backgroundStyles]="backgroundStyles"
      >
      </fl-picture>
      <div class="Wrapper">
        <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
          <fl-col
            [col]="12"
            [colTablet]="6"
            [colDesktopLarge]="12"
            [flAnimateOnScroll]="true"
            [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
            [flAnimateOnScrollDuration]="0.3"
          >
            <h2
              class="text-light text-xxlarge desktop:text-marketing_mega mb-mid tablet:mb-large desktop:mb-xlarge"
            >
              <ng-content
                select="fl-section-homepage-feature-title"
              ></ng-content>
            </h2>
          </fl-col>
          <fl-col
            class="TableCardContainer mobile:hide desktop_large:hide"
            [col]="12"
            [colTablet]="6"
          >
            <fl-picture
              class="TableCard"
              [srcTablet]="srcImageAnimationTablet"
              [boundedWidth]="true"
              [flAnimateOnScroll]="true"
              [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
              [flAnimateOnScrollDuration]="0.3"
              [flAnimateOnScrollDelay]="0.3"
            >
            </fl-picture>
          </fl-col>
        </fl-grid>
        <fl-grid>
          <fl-col
            class="mb-large tablet:mb-none"
            [col]="12"
            [colDesktopLarge]="6"
            [flAnimateOnScroll]="true"
            [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
            [flAnimateOnScrollDuration]="0.3"
            [flAnimateOnScrollDelay]="0.1"
          >
            <fl-grid>
              <fl-col
                [col]="12"
                [colTablet]="6"
              >
                <ng-content
                  select="fl-section-homepage-feature-top-left"
                ></ng-content>
              </fl-col>
              <fl-col
                [col]="12"
                [colTablet]="6"
              >
                <ng-content
                  select="fl-section-homepage-feature-top-right"
                ></ng-content>
              </fl-col>
            </fl-grid>
            <fl-grid
              class="{{
                sectionHomepageFeatureFooterComponent ? 'mb-xxsmall' : 'mb-none'
              }} {{
                sectionHomepageFeatureFooterComponent
                  ? 'tablet:mb-small'
                  : 'tablet:mb-none'
              }}"
            >
              <fl-col
                [col]="12"
                [colTablet]="6"
              >
                <ng-content
                  select="fl-section-homepage-feature-bottom-left"
                ></ng-content>
              </fl-col>
              <fl-col
                [col]="12"
                [colTablet]="6"
              >
                <ng-content
                  select="fl-section-homepage-feature-bottom-right"
                ></ng-content>
              </fl-col>
            </fl-grid>
            @if (sectionHomepageFeatureFooterComponent) {
              <ng-content
                select="fl-section-homepage-feature-footer"
              ></ng-content>
            }
          </fl-col>
          <fl-col
            class="AnimatedImage"
            [col]="12"
            [colDesktopLarge]="6"
          >
            <fl-picture
              class="MobileCard"
              [src]="srcImageAnimationMobile"
              [srcLargeDesktop]="srcImageAnimationLargeDesktop"
              [boundedWidth]="true"
              [flAnimateOnScroll]="true"
              [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
              [flAnimateOnScrollDelay]="0.3"
              [flAnimateOnScrollDuration]="0.3"
            >
            </fl-picture>

            <fl-picture
              class="mobile:show"
              [src]="srcBackgroundMobile"
            >
            </fl-picture>
          </fl-col>
        </fl-grid>
      </div>
    </fl-container>
  `,
  styleUrls: ['./section-homepage-feature.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHomepageFeatureComponent {
  AnimationBehavior = AnimationBehavior;
  ContainerSize = ContainerSize;
  VerticalAlignment = VerticalAlignment;

  readonly backgroundStyles: PictureBackgroundImageStyles = {
    position: 'center top',
    positionDesktop: 'center',
    objectFitTablet: PictureBgImageObjectFit.CONTAIN,
    objectFitDesktop: PictureBgImageObjectFit.NONE,
    objectFitDesktopLarge: PictureBgImageObjectFit.NONE,
  };

  @Input() srcImageAnimationMobile: string;
  @Input() srcImageAnimationTablet: string;
  @Input() srcImageAnimationLargeDesktop: string;

  @Input() srcBackgroundMobile: string;
  @Input() srcBackgroundTablet: string;
  @Input() srcBackgroundLargeDesktop: string;

  @ContentChild(SectionHomepageFeatureFooterComponent)
  sectionHomepageFeatureFooterComponent: SectionHomepageFeatureFooterComponent;
}
